/* eslint-disable @typescript-eslint/no-explicit-any */ // TODO: Remove this disable and fix all any types in this file
import { createMechanicPartsListQuery } from "@/abilities/mechanic/graphql/mechanics-partslist.query";
import { generateDynamicSearchMechanicArticlesOnlyAggregationsQuery } from "@/abilities/mechanic/helpers/article-search-helper";
import { ADD_TO_COLLECTION_COL_NAME } from "@/abilities/mechanic/pages/article-search/articles-list-data-source.ts";
import { AccessFeature } from "@/shared/access-control/access-control.ts";
import { useHasAccess } from "@/shared/access-control/composables/use-has-access.ts";
import { convertAggregationsResult } from "@/shared/aggregations/aggregations-resolver";
import { loadMore } from "@/shared/components/scroll/lazy-loading-fetch";
import { SortEntry } from "@/shared/components/sort-dropdown/sort-dropdown.model";
import {
    QDataSource,
    QIT_PREVIEW_IMAGE_COLUMN,
    QIT_PREVIEW_IMAGE_ROW_FIELD,
    QTableRowType,
} from "@/shared/components/table/q-data-source";
import { useDataDisplayConfig } from "@/shared/data-display-config/composable/data-display-config";
import { getDataDisplayValue } from "@/shared/data-display-config/composable/data-display.helper";
import { DataDisplayConfigId } from "@/shared/data-display-config/composable/data-display.model";
import { isLinkDisplayField } from "@/shared/data-display-config/data-display.utils.ts";
import { getSortEntries } from "@/shared/environment/filter/filter-types";
import { generateOrGroupsByFacetsSelected } from "@/shared/facets/facets-filter-generator";
import { Facet, FacetSelected } from "@/shared/facets/facets.model";
import { localizeSearchFields } from "@/shared/search/localize-search-fields";
import { DataSearchConfigId, useSearchConfig } from "@/shared/search/search-config";
import { generateSearchFieldsFilterClauseRegex } from "@/shared/search/search-fields-filter-clause";
import { SearchContext } from "@/shared/search/search.types";
import { consumerClient } from "@/shared/services/apollo-clients/consumer-client";
import {
    ContentSort,
    InputMaybe,
    LanguageWithWildcard,
    MechanicEdge,
    MechanicFilter,
} from "@/shared/services/graphql/generated/consumer-graph-types";
import { useLanguageStore } from "@/shared/store/language-store";
import { useQuery } from "@vue/apollo-composable";
import { QTableProps } from "quasar";
import { computed, ComputedRef, ref, Ref } from "vue";
import { useI18n } from "vue-i18n";

export const useMechanicArticlesDataSource = (
    searchContext: Ref<SearchContext>,
    selected: Ref<FacetSelected[]>,
    sort: Ref<SortEntry | undefined>,
    facet: Ref<Facet | undefined>,
    blobId: Ref<string | undefined>
) => {
    const { t } = useI18n();
    const { hasAccess } = useHasAccess({ featureID: AccessFeature.spare_parts });
    const languageStore = useLanguageStore();

    const {
        result: dataDisplayResult,
        loading: dataConfigLoading,
        error: dataConfigError,
        priorityDataDisplayFields,
    } = useDataDisplayConfig(DataDisplayConfigId.parts, t);

    const { result: mechanicArticlesSearchConfigResult } = useSearchConfig(DataSearchConfigId.parts);

    const generateMechanicArticleFilterVariables = (
        searchContext: SearchContext,
        selected: FacetSelected[],
        sort: SortEntry | undefined,
        blob: string | undefined
    ) => {
        const searchPhrase = searchContext.text ?? "";
        const filteredSearchFields = localizeSearchFields(
            mechanicArticlesSearchConfigResult.value?.searchEntries ?? [],
            languageStore.dataQueryLanguages
        );
        const searchFilterClause = generateSearchFieldsFilterClauseRegex(searchPhrase, filteredSearchFields, true);

        const mechanicFilter: MechanicFilter = {};

        //The wildcard search with * behaves like empty-string search, otherwise the regex will not fit and no results will be returned (see QIT-2531).
        if (searchContext.text && searchContext.text !== "*") {
            mechanicFilter.orGroup = [
                {
                    regex: {
                        articleNumber: `/${searchContext.text}.*/i`,
                    },
                    boost: 10,
                },

                ...(searchFilterClause.orGroup ?? []),
            ];
        }

        const copySelected = [...selected];
        const orGroups = generateOrGroupsByFacetsSelected(copySelected);

        if (orGroups.length > 0) {
            (mechanicFilter as any).andGroup = orGroups;
        }
        if (blob)
            mechanicFilter.image = {
                blobId: blob,
            };

        const sortEntries = getSortEntries(sort, languageStore.dataQueryLanguages);

        return {
            filter: mechanicFilter,
            acceptedLanguages: languageStore.dataQueryLanguages,
            sortEntries,
            productId: searchContext.product,
            assetId: searchContext.asset,
        };
    };

    const filter = computed<
        | { filter: InputMaybe<MechanicFilter> }
        | {
              mechanicFilter: MechanicFilter;
              acceptedLanguages: LanguageWithWildcard[];
              sort: ContentSort;
          }
        | undefined
    >(() => {
        return generateMechanicArticleFilterVariables(searchContext.value, selected.value, sort.value, blobId.value);
    });

    const FIRST_LOAD = 100;
    const FETCH_MORE_LOAD = 50;

    const queryDocument = computed(() => {
        if (dataDisplayResult.value)
            return createMechanicPartsListQuery({
                datafieldsFragment: dataDisplayResult.value.datafieldsFragment.datafields,
                teaserDatafieldsFragment: dataDisplayResult.value.datafieldsFragment.localizationDatafields,
                withPreviewImages: true,
            });

        return createMechanicPartsListQuery({});
    });

    const mechanicFilter = computed(() => ({
        ...filter.value,
        productId: searchContext.value.product,
        assetId: searchContext.value.asset,
        acceptedLanguages: languageStore.dataQueryLanguages,
        first: FIRST_LOAD,
        sort: getSortEntries(sort.value, languageStore.dataQueryLanguages),
    }));

    const {
        loading: mechanicArticlesLoading,
        error: mechanicArticlesError,
        fetchMore,
        result: mechanicArticlesResult,
    } = useQuery<any>(
        queryDocument,
        mechanicFilter,
        computed(() => ({
            enabled: !!(dataDisplayResult.value && sort.value),
        }))
    );

    const hasMoreResults = ref(true);

    function loadMoreArticles() {
        // do not load more articles if there are no more results
        if (!mechanicArticlesResult.value) {
            return;
        }
        // get the last article from the current result
        const lastArticle =
            mechanicArticlesResult.value.mechanics.mechanics[
                mechanicArticlesResult.value.mechanics.mechanics.length - 1
            ];
        // if there is no last article or no cursor, we cannot load more
        if (!lastArticle || !lastArticle.cursor) {
            return;
        }
        // load more articles
        loadMore(fetchMore, lastArticle.cursor, FETCH_MORE_LOAD).then((data) => {
            hasMoreResults.value = data?.data.mechanics.mechanics.length === FETCH_MORE_LOAD;
        });
    }

    const mechanicArticles = computed(() => {
        return mechanicArticlesResult.value?.mechanics?.mechanics ?? [];
    });

    const total = computed(() => {
        return mechanicArticlesResult.value?.mechanics?.total;
    });

    const loading = computed(() => {
        return mechanicArticlesLoading.value || dataConfigLoading.value;
    });

    const error = computed(() => {
        return dataConfigError.value || mechanicArticlesError.value;
    });

    const columns = computed(() => {
        if (!dataDisplayResult.value || dataDisplayResult.value.dataDisplayFields.length === 0) {
            //if no datadisplayfields configured, we need an empty column so that the rows are shown
            return [{ name: "", label: "", field: "", align: "left" }];
        }

        let result = dataDisplayResult.value?.dataDisplayFields.map((field) => {
            return {
                name: field.key,
                label: field.title,
                field: field.key,
                align: "left",
            };
        });

        if (hasAccess.value)
            result = [...result, { name: ADD_TO_COLLECTION_COL_NAME, label: "", field: "", align: "left" }];

        return [
            {
                name: QIT_PREVIEW_IMAGE_COLUMN,
                field: QIT_PREVIEW_IMAGE_ROW_FIELD,
                label: "",
            },
            ...result,
        ];
    });

    const mobileColumns = computed(() => {
        let columns = priorityDataDisplayFields.value.map((displayField) => {
            return {
                name: displayField.key,
                field: displayField.key,
                label: displayField.title,
                align: "left",
            };
        });

        if (hasAccess.value)
            columns = [...columns, { name: ADD_TO_COLLECTION_COL_NAME, label: "", field: "", align: "left" }];

        return columns;
    });

    const dataRows = computed<QTableRowType[]>(() => {
        return mechanicArticles.value
            ?.filter((edge: MechanicEdge) => edge.node)
            .map((edge: MechanicEdge) => {
                const result: Partial<QTableRowType> = {
                    id: edge.node.id,
                    [QIT_PREVIEW_IMAGE_ROW_FIELD]: edge.node.previewImage?.url,
                };

                dataDisplayResult.value?.dataDisplayFields
                    .filter((field) => field.key !== "id")
                    .forEach((dataDisplayField) => {
                        const dataValue = getDataDisplayValue(dataDisplayField, edge.node);
                        const isLink = isLinkDisplayField(
                            dataDisplayField.fieldTypeInfo?.fieldType,
                            dataValue.formattedValue
                        );
                        // Store as object with formattedValue and optional symbols
                        result[dataDisplayField.key] = {
                            formattedValue: dataValue.formattedValue,
                            symbols: dataValue.symbols,
                            isLink,
                            colorClass: dataValue.colorClass,
                        };
                    });

                return result as QTableRowType;
            });
    });

    const qDataSource: ComputedRef<QDataSource> = computed(() => {
        return {
            columns: columns.value as QTableProps["columns"],
            mobileColumns: mobileColumns.value as QTableProps["columns"],
            rows: dataRows.value,
        };
    });

    const resultListEmpty = computed(() => {
        return dataRows.value?.length === 0;
    });

    const fetchAggregation = async (searchPhrase: string) => {
        if (!facet.value) return {};

        const aggregationResult = await consumerClient.query({
            query: generateDynamicSearchMechanicArticlesOnlyAggregationsQuery(facet.value, searchPhrase),
            variables: filter.value,
        });

        const convertedAggregations = convertAggregationsResult(
            facet.value,
            aggregationResult.data["mechanics"].aggregations
        );

        return convertedAggregations[facet.value.id];
    };

    return {
        loading,
        error,
        qDataSource,
        resultListEmpty,
        hasMoreResults,
        loadMoreArticles,
        total,
        fetchAggregation,
        mechanicArticlesResult,
    };
};
